/**
 * CoachOverlay — onboarding spotlight driven by the copilot's `highlight` tool.
 *
 * Self-registers a `highlight` (and `clearHighlight`) action on the actionBridge,
 * so the agent can point at any element tagged with `data-coach="<id>"`. Renders
 * a dimming spotlight around the target plus a tooltip bubble. If the anchor
 * isn't on screen, it degrades to a centered text-only bubble.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { registerBridge } from '../../services/agent/actionBridge';
import useAnchorRect from './useAnchorRect';
import './CoachOverlay.css';

export default function CoachOverlay() {
  const [target, setTarget] = useState(null); // { coachId, message }

  const show = useCallback(({ coachId, message } = {}) => {
    setTarget({ coachId, message });
    return { ok: true, coachId };
  }, []);
  const clear = useCallback(() => { setTarget(null); return { ok: true }; }, []);

  // Register highlight tools on the shared action bridge.
  useEffect(() => {
    registerBridge({ actions: { highlight: show, clearHighlight: clear } });
  }, [show, clear]);

  // Track the anchor's position (re-measures on layout/transition changes).
  const rect = useAnchorRect(target?.coachId);

  if (!target) return null;

  const pad = 6;
  const spotStyle = rect && {
    left: rect.left - pad,
    top: rect.top - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };

  const bubbleStyle = rect
    ? {
        left: Math.min(Math.max(12, rect.left), window.innerWidth - 300),
        top: rect.bottom + 12 < window.innerHeight - 130 ? rect.bottom + 12 : Math.max(12, rect.top - 130),
      }
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="coach-overlay" onClick={clear}>
      {spotStyle && <div className="coach-spot" style={spotStyle} />}
      <div className="coach-bubble" style={bubbleStyle} onClick={(e) => e.stopPropagation()}>
        <div className="coach-bubble-text">{target.message}</div>
        <button className="coach-bubble-dismiss" onClick={clear}>Got it</button>
      </div>
    </div>
  );
}
